import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { useDrag } from '@use-gesture/react';
import useForkRef from '../../hooks/useForkRef';

const Drag = React.forwardRef((props, ref) => {
  const {
    children,
    lockAxis = 'none',
    onDrag,
    onDragEnd,
    onSwipe,
    onSwipeEnd,
    options,
    on
  } = props;

  const ownRef = useRef();
  const handleRef = useForkRef(
    ownRef,
    children.ref,
  );

  const gestureOptions = {
    enabled: on,
    preventDefault: false,
    filterTaps: true,
    threshold: [10, 10],
    rubberband: true,
    ...options
  };

  const dragBind = useDrag(({
    down,
    active,
    tap,
    swipe: [swipeX, swipeY],
    movement: [mx, my],
    offset: [ox, oy],
    event
  }) => {
    if (tap) return;

    const shouldPreventDefault = () => {
      if (lockAxis === 'vertical' && Math.abs(my) > Math.abs(mx)) {
        return true;
      }
      if (lockAxis === 'horizontal' && Math.abs(mx) > Math.abs(my)) {
        return true;
      }
      if (lockAxis === 'none' && (Math.abs(mx) > 10 || Math.abs(my) > 10)) {
        return true;
      }
      return false;
    };

    if (down && active && shouldPreventDefault() && event) {
      event.preventDefault();
    }

    if (onSwipe && swipeX !== 0 && lockAxis !== 'vertical') {
      onSwipe({
        down, active, movement: { mx, my }, offset: { ox, oy }, swipeX
      });
      if (ownRef.current) {
        ownRef.current.style.transform = `translate3d(${swipeX * 100 + mx}px, 0px, 0)`;
      }
      if (onSwipeEnd) {
        onSwipeEnd({
          down, active, movement: { mx, my }, offset: { ox, oy }, swipeX, swipeY
        });
      }
    } else if (onSwipe && swipeY !== 0 && lockAxis !== 'horizontal') {
      onSwipe({
        down, active, movement: { mx, my }, offset: { ox, oy }, swipeY
      });
      if (ownRef.current) {
        ownRef.current.style.transform = `translate3d(0px, ${swipeY * 100 + my}px, 0)`;
      }
      if (onSwipeEnd) {
        onSwipeEnd({
          down, active, movement: { mx, my }, offset: { ox, oy }, swipeX, swipeY
        });
      }
    } else {
      const noHorizontal = lockAxis === 'vertical';
      const noVertical = lockAxis === 'horizontal';
      if (onDrag) {
        onDrag({ down, active, movement: { mx, my }, offset: { ox, oy } });
      }
      if (ownRef.current) {
        ownRef.current.style.transform = `translate3d(${(down && active && !noHorizontal ? mx : 0)}px, ${(down && active && !noVertical ? my : 0)}px, 0)`;
      }
      if (onDragEnd) {
        onDragEnd({ down, active, movement: { mx, my }, offset: { ox, oy } });
      }
    }
  }, gestureOptions);

  let touchAction = 'none';
  if (lockAxis === 'vertical') {
    touchAction = 'pan-x';
  } else if (lockAxis === 'horizontal') {
    touchAction = 'pan-y';
  }

  const childrenProps = {
    ref: handleRef,
    style: {
      transform: 'translate3d(0px, 0px, 0)',
      position: 'relative',
      touchAction
    },
    ...dragBind(),
  };
  return React.cloneElement(children, childrenProps);
});

Drag.displayName = 'Drag';

export { Drag };